import React, { useState, useEffect } from 'react';
import galleryImagesList from '../../gallery-images.json';

const ImagePickerModal = ({ 
  isOpen, 
  onClose, 
  onSelect, 
  currentValue, 
  multiple = false,
  title = "Odaberi fotografiju"
}) => {
  // Hardcoded list based on public/img/photos
  const photos = [
    '/img/photos/hero1d.jpg', '/img/photos/hero2d.jpg', '/img/photos/hero3d.jpg',
    '/img/photos/hero1m.jpg', '/img/photos/hero2m.jpg',
    '/img/photos/default.jpg'
  ];

  // Load from automated gallery images list
  const galleryImages = galleryImagesList.map(img => `/img/gallery/${img}`);

  const images = [...photos, ...galleryImages];
  
  const [selectedImages, setSelectedImages] = useState([]);

  useEffect(() => {
    if (isOpen) {
      if (multiple) {
        setSelectedImages(Array.isArray(currentValue) ? currentValue : (currentValue ? [currentValue] : []));
      } else {
        setSelectedImages(currentValue ? [currentValue] : []);
      }
    }
  }, [isOpen, currentValue, multiple]);

  if (!isOpen) return null;

  const handleImageClick = (fullPath) => {
    if (multiple) {
      if (selectedImages.includes(fullPath)) {
        setSelectedImages(selectedImages.filter(img => img !== fullPath));
      } else {
        setSelectedImages([...selectedImages, fullPath]);
      }
    } else {
      setSelectedImages([fullPath]);
      onSelect(fullPath);
      onClose();
    }
  };

  const handleConfirm = () => {
    if (multiple) {
      onSelect(selectedImages);
    } else {
      onSelect(selectedImages[0] || '');
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 md:p-8">
      {/* Overlay */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-5xl bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] animate-slide-up">
        {/* Header */}
        <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-white sticky top-0 z-10">
          <div>
            <h2 className="text-xl font-black text-black uppercase tracking-tight">{title}</h2>
            <p className="text-[10px] text-black font-bold uppercase tracking-widest mt-1">
              {multiple ? 'Kliknite na fotografije redoslijedom kojim želite da se prikažu (1, 2, 3...)' : 'Odaberite jednu fotografiju'}
            </p>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <svg className="w-6 h-6 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1 bg-gray-50/50">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {images.map((img) => {
              const fullPath = img;
              const isSelected = selectedImages.includes(fullPath);
              
              return (
                <button
                  key={img}
                  type="button"
                  onClick={() => handleImageClick(img)}
                  className={`group relative aspect-square rounded-2xl overflow-hidden border-2 transition-all duration-300 ${
                    isSelected ? 'border-black scale-[0.98] shadow-inner' : 'border-transparent hover:border-gray-200 shadow-sm'
                  }`}
                >
                  <img 
                    src={fullPath} 
                    alt={img} 
                    className={`w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 ${isSelected ? 'opacity-80' : ''}`} 
                  />
                  
                  {/* Selection Overlay */}
                  {isSelected && (
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center ring-4 ring-inset ring-black/20">
                      <div className="bg-black text-white rounded-full w-10 h-10 flex items-center justify-center shadow-2xl border-2 border-white animate-scale-in">
                        {multiple ? (
                          <span className="text-sm font-black italic">{selectedImages.indexOf(fullPath) + 1}</span>
                        ) : (
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Hover Info */}
                  <div className="absolute bottom-0 inset-x-0 p-2 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="text-[8px] text-white font-bold truncate block">{img.split('/').pop()}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        {multiple && (
          <div className="p-6 border-t border-gray-100 bg-white flex items-center justify-between">
            <span className="text-[10px] font-bold text-black uppercase tracking-widest">
              Odabrano: {selectedImages.length}
            </span>
            <div className="flex gap-3">
              <button 
                onClick={onClose}
                className="px-6 py-2.5 rounded-xl text-[10px] font-bold uppercase tracking-widest text-black hover:bg-gray-50 transition-all border border-gray-200"
              >
                Otkaži
              </button>
              <button 
                onClick={handleConfirm}
                className="px-6 py-2.5 rounded-xl text-[10px] font-bold uppercase tracking-widest bg-black text-white hover:bg-gray-800 transition-all shadow-lg"
              >
                Spremi Odabir
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImagePickerModal;
